import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx-js-style";
import Swal from "sweetalert2";
import QRCode from "qrcode";
import whatsappIcon from "../Images/whatsapp.png";

// Gender from NRIC: last digit even = Female, odd = Male
const getGenderFromNric = (nric) => {
  const digits = String(nric || "").replace(/\D/g, "");
  if (!digits) return "";
  return parseInt(digits.slice(-1), 10) % 2 === 0 ? "Female" : "Male";
};

// Calls for the same event made while one is still loading share that request.
// Loading an event reads every attendee in Firestore, so this stops React StrictMode's
// double mount (in development) from paying for the same reads twice.
const eventRequests = new Map();

const loadEvent = (id) => {
  if (!eventRequests.has(id)) {
    const request = fetch(`/api/events/${id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .finally(() => eventRequests.delete(id));
    eventRequests.set(id, request);
  }
  return eventRequests.get(id);
};

function EventDetails({ onEventCreated }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [eventVenue, setEventVenue] = useState("");
  const [headerImage, setHeaderImage] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [invitationMessage, setinvitationMessage] = useState([]);
  const [selectedMessageId, setSelectedMessageId] = useState("");
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [messageType, setMessageType] = useState("textonly");
  const [fileUrl, setFileUrl] = useState("");
  const fileInputRef = useRef();
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrSrc, setQrSrc] = useState("");
  const headerImageInputRef = useRef();

  // Define mappings
  const RSVP_STATUS = {
    0: "Pending",
    1: "Attend",
    2: "Not Attend"
  };

  const formatYesNo = (value) => (value === true ? "Yes" : value === false ? "No" : "-");

  const CHECKIN_STATUS = {
    0: "Pending",
    1: "Checked In"
  };

  // Extracted fetch function
  const fetchEventData = async () => {
    setLoading(true); // start loading
    try {
      const data = await loadEvent(id);

      setEventName(data.event_name || "");
      setEventVenue(data.venue || "");
      setEventTime(data.event_time || "");
      setinvitationMessage(data.invite_messages || []);
      
      if (data.event_date) {
        const dateStr = new Date(data.event_date).toISOString().slice(0, 10);
        setEventDate(dateStr);
      } else {
        setEventDate("");
      }

      setAttendees(data.attendees?.map(a => ({
        name: a.name || "",
        nric: a.nric || "",
        gender: a.gender || "",
        email: a.email || "",
        phone: a.phone || "",
        table: a.table || "",
        dept: a.dept || "",
        user_id: a._id || "",
        attendee_id: a.attendee_id || "",
        rsvp: {
          status: a.rsvp?.status ?? 0,
          best_dress: a.rsvp?.best_dress ?? null,
          vegetarian: a.rsvp?.vegetarian ?? null,
          whatsapp: a.rsvp?.whatsapp ?? null,
        },
        checkin: { status: a.checkin?.status ?? 0 },
        // rsvp: a.rsvp?.status ?? 0,
        // checkin: a.checkin?.status ?? 0,
        message: a.message || {},
      })) || []);
    } catch (error) {
      console.error("Failed to fetch event:", error);
    } finally {
      setLoading(false); // stop loading
    }
  };

  const formatName = (name) => {
    if (!name) return "";
    return name
      .trim()
      .toLowerCase()
      .split(/\s+/) // split by space(s)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };  

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      setEventName("");
      setEventVenue("");
      setAttendees([]);
      setinvitationMessage([]);
      setEventDate("");
      setEventTime("");
      setHeaderImage("");
      setSelectedMessageId("");
      setSelectedCheckbox([]);
      setMessageInput("");
      setMessageType("textonly");

      fetchEventData();
    }
  }, [id]);

  useEffect(() => {
    if (showQR && editingAttendee) {
      generateQrCode(id, editingAttendee).then(({ qrBase64 }) => {
        setQrSrc(qrBase64);
      });
    }
  }, [showQR, editingAttendee]);

  // Check for existing header image when page loads
  useEffect(() => {
    const checkHeaderImage = async () => {
      if (!id) return;

      try {
        const res = await fetch(`/api/events/${id}/check-header`);
        if (res.ok) {
          const data = await res.json();
          if (data.hasHeader && data.imagePath) {
            setHeaderImage(data.imagePath);
          }
        }
      } catch (error) {
        console.error('Error checking for header image:', error);
      }
    };

    checkHeaderImage();
  }, [id]);  

  // Reusable function to generate both QR Base64 and QR URL
  const generateQrCode = async (eventId, attendee) => {
    if (!attendee) return { qrBase64: null, qrUrl: null };

    const data = JSON.stringify({
      eventID: eventId,
      attendeeID: attendee.attendee_id,
      name: attendee.name,
      nric: attendee.nric,
      phone: attendee.phone,
    });

    let qrBase64 = null;
    try {
      qrBase64 = await QRCode.toDataURL(data); // base64 image for modal
    } catch (err) {
      console.error("QR base64 generate error:", err);
    }

    // URL version for sending via API
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data)}&size=200x200`;

    return { qrBase64, qrUrl };
  };
  
  // ------------------------------onsend-----------------------------------
  const handleSendMessage = async () => {
    if (selectedCheckbox.length === 0) {
      Swal.fire({
        text: 'Please tick at least one checkbox to send an invitation',
        icon: 'warning'
      });
      return;
    }

    if (!messageInput.trim()) {
      Swal.fire({
        text: 'Please enter the message to be sent',
        icon: 'warning'
      });
      return;
    }

    if ((messageType === "image" || messageType === "pdf") && (!fileUrl.trim())) {
      Swal.fire({
        text: `Please enter URL for the ${messageType}`,
        icon: 'warning'
      });
      return;
    }

    setLoading(true); // start loading

    try {
      // Loop sequentially (await each one)
      for (const userid of selectedCheckbox) {
        const attendee = attendees.find(a => a.user_id === userid);
        if (!attendee || !attendee.phone) continue; // Skip if not found or missing phone
  
        let data = { phone_number: String(attendee.phone) };

        let finalMessage = messageInput;

        // Replace placeholders with actual attendee data
        finalMessage = finalMessage.replace(/{name}/g, attendee.name);
        // finalMessage = finalMessage.replace("{name}", attendee.name);
  
        if (messageType === "textonly") {
          data.message = finalMessage;
        } else if (messageType === "image") {
          data.type = "image";
          data.url = fileUrl;
          data.message = finalMessage;
        } else if (messageType === "pdf") {
          data.type = "document";
          data.url = fileUrl;
          data.mimetype = "application/pdf";
          data.filename = `${eventName}.pdf`;
          data.message = finalMessage;
        }
  
        try {
          const response = await fetch("/api/send-message", {
          // const response = await fetch("https://onsend.io/api/v1/send", {
            method: "POST",
            headers: {
              "Accept": "application/json",
              // "Authorization": "Bearer da9e5b27dc977c3e1c1c1b101d84b547e4372e8ec233d6116e1deea52c5d081d",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          });
  
          const result = await response.json();
          console.log(`Message sent to ${attendee.name}:`, result);
  
          await fetch(` /api/events/${id}/attendees/${attendee.user_id}/message`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message: result }),
          });
        } catch (error) {
          console.error(`Error sending message to ${attendee.name}:`, error);
        }
      }
  
      // refresh after all loop done
      await fetchEventData();

      // ✅ Show success alert after all messages are sent
      Swal.fire({
        text: 'All RSVP have been sent successfully!',
        icon: 'success'
      });
  
    } finally {
      setLoading(false); // stop loading AFTER loop finishes
    }
  };

  // ------------------------------QR-----------------------------------
  const handleSendQR = async () => {
    if (selectedCheckbox.length === 0) {
      Swal.fire({ text: 'Please tick at least one checkbox to send ticket', icon: 'warning' });
      return;
    }
  
    // if (!messageInput.trim()) {
    //   Swal.fire({ text: 'Please enter the message to be sent', icon: 'warning' });
    //   return;
    // }
  
    // if ((messageType === "image" || messageType === "pdf") && (!fileUrl.trim())) {
    //   Swal.fire({ text: `Please enter URL for the ${messageType}`, icon: 'warning' });
    //   return;
    // }

    const checkedAttendees = selectedCheckbox
      .map(userId => attendees.find(a => a.user_id === userId))
      .filter(Boolean);

    // Choose how to send the ticket
    const { isConfirmed: chooseEmail, isDenied: chooseWhatsapp } = await Swal.fire({
      text: `How would you like to send the ticket to ${checkedAttendees.length} attendee(s)?`,
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "📧 Email",
      denyButtonText: `
        <span style="display:flex;align-items:center;gap:6px;">
          <img src="${whatsappIcon}" alt="WhatsApp" style="width:18px;height:18px;" />
          WhatsApp
        </span>
      `,
      denyButtonColor: "#25D366",
      cancelButtonText: "Cancel"
    });
    if (!chooseEmail && !chooseWhatsapp) return;

    const sendMethod = chooseWhatsapp ? "whatsapp" : "email";
    const methodLabel = sendMethod === "whatsapp" ? "WhatsApp" : "Email";
    const contactField = sendMethod === "whatsapp" ? "phone" : "email";
    const contactLabel = sendMethod === "whatsapp" ? "phone number" : "email";

    // Only send to attendees that have the required contact
    const hasContact = (a) => a[contactField] && String(a[contactField]).trim() && String(a[contactField]).trim() !== "-";
    const selectedAttendees = checkedAttendees.filter(hasContact);
    const missingContact = checkedAttendees.filter(a => !hasContact(a));

    if (selectedAttendees.length === 0) {
      Swal.fire({ text: `None of the selected attendees has a ${contactLabel}`, icon: 'warning' });
      return;
    }

    const { isConfirmed } = await Swal.fire({
      html: `Send ticket via ${methodLabel} to <b>${selectedAttendees.length}</b> attendee(s)?` +
        (missingContact.length
          ? `<br><br>Skipped (no ${contactLabel}):<br><b>${missingContact.map(a => a.name).join(", ")}</b>`
          : ""),
      icon: missingContact.length ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: `Send ${methodLabel}`,
      confirmButtonColor: sendMethod === "whatsapp" ? "#25D366" : undefined,
      cancelButtonText: "Cancel"
    });
    if (!isConfirmed) return;

    const total = selectedAttendees.length;

    const sendTicket = async (attendee) => {
      let finalMessage = messageInput.replace("{name}", attendee.name);

      const { qrUrl } = await generateQrCode(id, attendee);

      const data = {
        phone_number: attendee.phone,
        type: "image",
        url: qrUrl,
        message: finalMessage,
      };

      const endpoint = sendMethod === "whatsapp"
        ? `/api/send-message/events/${id}/attendees/${attendee.attendee_id}`
        : `/api/send-email/events/${id}/attendees/${attendee.attendee_id}`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to send to ${attendee.name}`);
      }

      const result = await response.json();
      console.log(`Message sent to ${attendee.name} via ${sendMethod}:`, result);

      await fetch(`/api/events/${id}/attendees/${attendee.user_id}/message`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: result }),
      });
    };

    // Progress popup
    const BATCH_SIZE = 10;
    const BATCH_DELAY_MS = 1000;
    const totalBatches = Math.ceil(total / BATCH_SIZE);
    let sentCount = 0;
    let failedList = [];

    const updateProgress = (batchNo) => {
      const done = sentCount + failedList.length;
      const percent = total ? Math.round((done / total) * 100) : 0;
      const container = Swal.getHtmlContainer();
      if (!container) return;
      container.querySelector("#qr-progress-text").textContent = `${done} / ${total}`;
      container.querySelector("#qr-progress-bar").style.width = `${percent}%`;
      container.querySelector("#qr-progress-detail").textContent =
        `Batch ${batchNo} of ${totalBatches} · Sent: ${sentCount} · Failed: ${failedList.length}`;
    };

    Swal.fire({
      title: `Sending tickets via ${methodLabel}`,
      html: `
        <div style="font-size:22px;font-weight:bold;margin-bottom:8px;" id="qr-progress-text">0 / ${total}</div>
        <div style="width:100%;height:12px;background:#e5e7eb;border-radius:6px;overflow:hidden;">
          <div id="qr-progress-bar" style="width:0%;height:100%;background:#9333ea;transition:width 0.3s;"></div>
        </div>
        <div style="margin-top:8px;font-size:13px;color:#6b7280;" id="qr-progress-detail">Batch 0 of ${totalBatches}</div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    // Send in batches of 10 (each batch in parallel)
    for (let i = 0; i < total; i += BATCH_SIZE) {
      const batch = selectedAttendees.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(batch.map(sendTicket));

      results.forEach((res, idx) => {
        if (res.status === "fulfilled") {
          sentCount++;
        } else {
          console.error(`Error sending message to ${batch[idx].name}:`, res.reason);
          failedList.push(batch[idx].name);
        }
      });

      updateProgress(i / BATCH_SIZE + 1);

      // Pause between batches to avoid rate limits
      if (i + BATCH_SIZE < total) {
        await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
      }
    }

    // await fetchEventData(); // refresh after all messages

    // ✅ Show one final alert
    if (failedList.length === 0) {
      Swal.fire({
        text: `All ${total} tickets have been sent successfully!`,
        icon: "success",
      });
    } else {
      Swal.fire({
        title: "Some messages failed",
        html: `Sent: <b>${sentCount}</b> / ${total}<br>Failed to send to:<br><b>${failedList.join(", ")}</b>`,
        icon: "error",
      });
    }
  };

  // --------------------------------twilio-------------------------------------
  // const handleSendMessage = async (messageTemplate) => {
  //   if (selectedCheckbox.length === 0) {
  //     Swal.fire({
  //       text: 'Please tick at least one checkbox to send an invitation',
  //       icon: 'warning'
  //     });
  //     return;
  //   }
  
  //   // Loop over selected IDs
  //   for (const userid of selectedCheckbox) {
  //     const attendee = attendees.find(a => a.user_id === userid);
  //     if (!attendee || !attendee.phone) continue; // Skip if not found or missing phone
  
  //     const data = {
  //       body: messageTemplate,
  //       from: "whatsapp:+14155238886",
  //       to: `whatsapp:+${attendee.phone}`,
  //       mediaUrl: ["https://images.lifestyleasia.com/wp-content/uploads/sites/6/2025/06/05121015/twice-2025-ticket-price-Singapore-Indoor-Stadium-stage.jpg"]
  //       // mediaUrl: ["https://ontheline.trincoll.edu/images/bookdown/sample-local-pdf.pdf"]
  //     };
  
  //     try {
  //       const response = await fetch(`/api/events/${id}/attendees/${attendee.user_id}/send-message`, {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json"
  //         },
  //         body: JSON.stringify(data),
  //       });
  
  //       const result = await response.json();
  //       console.log(`Message sent to ${attendee.name}:`, result);

  //       // await fetch(`/api/events/${id}/attendees/${attendee.user_id}/message`, {
  //       //   method: "PUT",
  //       //   headers: { "Content-Type": "application/json" },
  //       //   body: JSON.stringify({ 
  //       //     message: result
  //       //   }),
  //       // });
        
  //       // // After successful update, refresh event data
  //       await fetchEventData();
  //     } catch (error) {
  //       console.error(`Error sending message to ${attendee.name}:`, error);
  //     }
  //   }
  // };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    setLoading(true);
  
    try {
      const fileResult = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => resolve(evt.target.result);
        reader.onerror = reject;
  
        // Use readAsText for CSV, readAsArrayBuffer for Excel
        if (file.name.endsWith(".csv")) {
          reader.readAsText(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });

      const getRSVPCode = (value) => {
        if (!value) return 0;
        const normalized = value.toLowerCase().trim();
        if (normalized === "attend") return 1;
        if (normalized === "not attend") return 2;
        return 0; // pending or empty
      };
      
      const getCheckInCode = (value) => {
        if (!value) return 0;
        const normalized = value.toLowerCase().trim();
        if (normalized === "checked in") return 1;
        return 0; // pending or empty
      };

      // "Male"/"M" or "Female"/"F"; anything else (blank, "-") = not given
      const getGenderValue = (value) => {
        const normalized = String(value || "").toLowerCase().trim();
        if (normalized === "male" || normalized === "m") return "Male";
        if (normalized === "female" || normalized === "f") return "Female";
        return "";
      };

      // Blank or "-" (from a downloaded sheet) = no dept
      const getDeptValue = (value) => {
        const dept = String(value || "").trim();
        return dept === "-" ? "" : dept;
      };

      const getMessageObject = (value) => {
        if (!value) return {};
        const normalized = value.toLowerCase().trim();
        if (normalized === "sent successfully") return { success: true };
        if (normalized === "send failed") return { success: false };
        return {}; // "Not Sent" or anything else
      };
  
      const jsonData = await new Promise((resolve) => {
        setTimeout(() => {
          try {
            let rows = [];
            if (file.name.endsWith(".csv")) {
              // Parse CSV
              const workbook = XLSX.read(fileResult, { type: "string" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
            } else {
              // Parse Excel
              const data = new Uint8Array(fileResult);
              const workbook = XLSX.read(data, { type: "array" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              rows = XLSX.utils.sheet_to_json(worksheet, { raw: false });
            }
  
            // Only keep `nric` and `name` columns
            const filteredRows = rows.map(row => ({
              nric: String(row.nric || row.NRIC || ""),
              name: formatName(row["Name"] || row.name),
              gender: getGenderValue(row.Gender || row.gender),
              attendee_id: parseInt(row["Attendee ID"] || row.attendee_id || 0, 10) || 0,
              email: String(row.email || row.Email || ""),
              phone: String(row.phone || row.Phone || ""),
              table: String(row.table || row.Table || ""),
              dept: getDeptValue(row.Dept || row.dept || row.DEPT || row.Department || row.department),
              rsvp: { status: getRSVPCode(row["RSVP Status"] || row.rsvp_status) },
              checkin: { status: getCheckInCode(row["Check In Status"] || row.checkin_status) },
              message: getMessageObject(row["Send Status"] || row.send_status),
            }));            
  
            resolve(filteredRows);
          } catch (err) {
            console.error("Parsing error:", err);
            resolve([]);
          }
        }, 0);
      });
  
      // setAttendees(jsonData);
      // await handleUpdate(jsonData);
      console.log("Parsed rows:", jsonData);

      // ✅ Now upload to backend API
      const response = await fetch(`/api/events/${id}/update-attendees`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attendees: jsonData }),
      });

      const result = await response.json();
      console.log("Server response:", result);

      if (response.ok) {
        Swal.fire("Success", "Attendees updated successfully!", "success");
        // After successful update, refresh event data
        await fetchEventData();
      } else {
        Swal.fire("Error", result.error || "Failed to update event", "error");
      }
    
    } catch (error) {
      console.error("File upload error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    Swal.fire({
      text: "Are you sure? Reuploading an Excel file will delete all current attendee records.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Trigger file input click
        fileInputRef.current.click();
      }
    });
  };

  const handleHeaderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append("headerImage", file); // <-- key must match multer.single("headerImage")
  
    try {
      setLoading(true);
      const res = await fetch(`/api/events/${id}/upload-header`, {
        method: "POST",
        body: formData, // <-- let browser set content-type
      });
  
      const data = await res.json();
      console.log("Upload response:", data);

      if (data.success && data.imagePath) {
        // force reload by adding a timestamp
        setHeaderImage(data.imagePath + "?t=" + Date.now());
      }      
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setLoading(false);
    }
  };    

  // Submit event + attendees to backend
  const handleUpdate = async (newAttendee) => {
    if (!eventName || !eventDate) {
      alert("Please enter event name and date.");
      return;
    }

    const payload = {
      event_name: eventName,
      event_date: eventDate,
      event_time: eventTime,
      venue: eventVenue,
      attendees: newAttendee ?? attendees,
    };

    setLoading(true); // start loading

    try {
      const res = await fetch(`/api/events/${id}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to create event");

      Swal.fire({
        text: 'Event updated successfully!',
        icon: 'success'
      });

      // After successful update, refresh event data
      await fetchEventData();

      setIsAddMode(false);
      setEditingAttendee(null);
      setIsModalOpen(false);
      setShowQR(false);

      // Trigger refresh in Sidebar
      if (onEventCreated) onEventCreated();

    } catch (error) {
      console.error(error);
      Swal.fire({
        text: 'Error updating event',
        icon: 'error'
      });
    } finally {
      setLoading(false); // stop loading
    }
  };

  const handleSelectCheckbox = (id) => {
    setSelectedCheckbox((prev) => {
      if (prev.includes(id)) {
        // Remove if already selected
        return prev.filter((selectedId) => selectedId !== id);
      } else {
        // Add if not selected
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all attendee IDs
      const allIds = attendees.map((a) => a.user_id);
      setSelectedCheckbox(allIds);
    } else {
      // Deselect all
      setSelectedCheckbox([]);
    }
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);

    const sortedAttendees = [...attendees].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
  
      // Handle sorting for "Send Status"
      if (field === "send_status") {
        aValue = a.message?.success === true ? 1 : a.message?.success === false ? 2 : 3;
        bValue = b.message?.success === true ? 1 : b.message?.success === false ? 2 : 3;
      } else if (field === "rsvp") {
        aValue = a.rsvp?.status ?? 0;   // use 0 if undefined
        bValue = b.rsvp?.status ?? 0;
      } else if (field === "checkin") {
        aValue = a.checkin?.status ?? 0;
        bValue = b.checkin?.status ?? 0;
      }
  
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  
    setAttendees(sortedAttendees);
  };

  const handleAddAttendee = () => {
    setEditingAttendee({
      nric: "",
      gender: "",
      name: "",
      email: "",
      phone: "",
      table: "",
    });
    setIsAddMode(true);
    setIsModalOpen(true);
  };

  const handleSaveMessage = async () => {
    if (!messageInput.trim()) {
      Swal.fire({
        text: 'Please enter the invitation message',
        icon: 'warning'
      });
      return;
    }

    setLoading(true);

    try {
      const newMessage = {
        type: messageType,
        message: messageInput,
        url: fileUrl || "",
      };
  
      const res = await fetch(`/api/events/${id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newMessage),
      });
  
      if (res.ok) {
        Swal.fire({
          text: `Message saved successfully.`,
          icon: 'success'
        });
      } else {
        Swal.fire({
          text: `Failed to save message.`,
          icon: 'error'
        });
      }
    } catch (err) {
      Swal.fire({
        text: 'Something went wrong.',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMessage = async () => {
    if (!selectedMessageId) {
      Swal.fire({
        text: 'Please select a saved message',
        icon: 'warning'
      });
      return;
    };

    setLoading(true);
  
    const payload = {
      type: messageType,
      message: messageInput,
      url: fileUrl || "",
    };
  
    try {
      const res = await fetch(`/api/events/${id}/messages/${selectedMessageId}`, {
        method: "PUT", // make sure your backend handles PUT for updating
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
  
      if (res.ok) {
        Swal.fire({
          text: 'Message updated successfully.',
          icon: 'success'
        });
  
        // Update local state so dropdown reflects new content
        setinvitationMessage(
          invitationMessage.map((msg) =>
            msg._id === selectedMessageId ? { ...msg, ...payload } : msg
          )
        );
      } else {
        Swal.fire({
          text: 'Failed to update message.',
          icon: 'error'
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        text: 'Error updating message',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    const confirmDelete = await Swal.fire({
      text: "Confirm deletion of this event?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      reverseButtons: true,
    });
  
    if (!confirmDelete.isConfirmed) return;
  
    try {
      const res = await fetch(`/api/events/${id}/delete`, { method: "PUT" });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to delete event");
      }
  
      Swal.fire({
        text: "Event deleted successfully!",
        icon: "success",
      });

      // Trigger refresh in Sidebar
      if (onEventCreated) onEventCreated();
  
      // Redirect or refresh the page
      navigate("/Homepage");
    } catch (error) {
      console.error(error);
      Swal.fire({
        text: "Error deleting event: " + error.message,
        icon: "error",
      });
    }
  };

  const downloadExcel = (attendees) => {
    Swal.fire({
      title: "Download Options",
      html: `
        <div style="display:flex; flex-direction:column; gap:10px;">
          <button id="btnAll" class="swal2-confirm swal2-styled" style="background-color:#3085d6;">Download All</button>
          <button id="btnPending" class="swal2-confirm swal2-styled" style="background-color:#6c757d;">RSVP Pending / Not Attend</button>
          <button id="btnAttend" class="swal2-confirm swal2-styled" style="background-color:#28a745;">RSVP Attend</button>
          <button id="btnCancel" class="swal2-cancel swal2-styled" style="background-color:#d33;">Cancel</button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      didOpen: () => {
        document.getElementById("btnAll").addEventListener("click", () => {
          Swal.close();
          generateExcel(attendees);
        });

        document.getElementById("btnPending").addEventListener("click", () => {
          Swal.close();
          const filtered = attendees.filter(
            (a) => a.rsvp?.status === 0 || a.rsvp?.status === 2
          );
          generateExcel(filtered);
        });

        document.getElementById("btnAttend").addEventListener("click", () => {
          Swal.close();
          const filtered = attendees.filter((a) => a.rsvp?.status === 1);
          generateExcel(filtered);
        });

        document.getElementById("btnCancel").addEventListener("click", () => {
          Swal.close();
        });
      },
    });
  };

  const generateExcel = (filteredAttendees) => {
    const headers = [
      "No",
      "NRIC",
      "Name",
      "Gender",
      "Dept",
      "Attendee ID",
      "Email",
      "Phone",
      "Table",
      "RSVP Status",
      "Check In Status",
      "Send Status",
    ];

    const rows = filteredAttendees.map((a, i) => ({
      No: i + 1,
      NRIC: a.nric || "-",
      Name: a.name || "-",
      Gender: a.gender || getGenderFromNric(a.nric) || "-",
      Dept: a.dept || "-",
      "Attendee ID": a.attendee_id || "-",
      Email: a.email || "-",
      Phone: a.phone || "-",
      Table: a.table || "-",
      "RSVP Status": a.rsvp ? RSVP_STATUS[a.rsvp.status] : "-",
      "Check In Status": a.checkin ? CHECKIN_STATUS[a.checkin.status] : "-",
      "Send Status":
        a.message && Object.keys(a.message).length > 0
          ? a.message.success === true
            ? "Sent Successfully"
            : "Send Failed"
          : "Not Sent",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendees");
    XLSX.writeFile(workbook, "attendees.xlsx");
  };

  // Filtered attendees based on search
  const filteredAttendees = attendees.filter(attendee => {
    const term = search.toLowerCase();
    return (
      (attendee.name && attendee.name.toLowerCase().includes(term)) ||
      (attendee.attendee_id && attendee.attendee_id.toString().toLowerCase().includes(term))
    );
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div>

        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-black">Event Details</h1>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => handleUpdate()}
              className="w-40 font-bold bg-yellow-500 text-white py-2 rounded hover:bg-yellow-600 transition duration-200"
            >
              Update Event
            </button>
            &nbsp;&nbsp;
            <button
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={handleDeleteEvent}
            >
              Delete Event
            </button>
          </div>
        </div>

        <form className="space-y-4 pt-6 mb-7" onSubmit={(e) => e.preventDefault()}>
          {/* <div className="flex items-center space-x-4">
            <label htmlFor="ename" className="whitespace-nowrap">
              RSVP Link:
            </label>
            <input
              id="ename"
              type="text"
              placeholder="Event Name"
              value={`https://alra.hslnet.my/rsvp/${id}`}
              onChange={(e) => setEventName(e.target.value)}
              className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div> */}
          <div className="flex items-center space-x-4">
            <label htmlFor="ename" className="whitespace-nowrap">
              Name:
            </label>
            <input
              id="ename"
              type="text"
              placeholder="Event Name"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          {/* <div className="flex items-center space-x-6"> */}
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-6 flex-grow">
              <label htmlFor="edate" className="whitespace-nowrap">
                Date:
              </label>
              <input
                id="edate"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            <div className="flex items-center space-x-6 flex-grow">
              <label htmlFor="etime" className="whitespace-nowrap">
                Time:
              </label>
              <div className="flex-grow flex items-center gap-2">
                <div className="flex-grow flex items-center border border-gray-300 rounded focus-within:ring-2 focus-within:ring-blue-400 px-4 py-2">
                  <input
                    type="text"
                    value={eventTime.split(':')[0]?.split(' ')[0] || ''}
                    onChange={(e) => {
                      const hours = e.target.value.replace(/\D/g, '').slice(0, 2);
                      const minutes = eventTime.split(':')[1]?.split(' ')[0] || '';
                      const period = eventTime.split(' ')[1] || 'AM';
                      setEventTime(`${hours}:${minutes} ${period}`);
                    }}
                    placeholder="HH"
                    maxLength="2"
                    className="w-12 px-2 text-center focus:outline-none"
                  />
                  <span className="text-gray-600">:</span>
                  <input
                    type="text"
                    value={eventTime.split(':')[1]?.split(' ')[0] || ''}
                    onChange={(e) => {
                      const minutes = e.target.value.replace(/\D/g, '').slice(0, 2);
                      const hours = eventTime.split(':')[0] || '';
                      const period = eventTime.split(' ')[1] || 'AM';
                      setEventTime(`${hours}:${minutes} ${period}`);
                    }}
                    placeholder="MM"
                    maxLength="2"
                    className="w-12 px-2 text-center focus:outline-none"
                  />
                  <select
                    value={eventTime.split(' ')[1] || 'AM'}
                    onChange={(e) => {
                      const timeValue = eventTime.split(' ')[0] || ':';
                      setEventTime(`${timeValue} ${e.target.value}`);
                    }}
                    className="ml-auto focus:outline-none bg-transparent"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 flex-grow">
              <label htmlFor="evenue" className="whitespace-nowrap">
                Venue:
              </label>
              <input
                id="evenue"
                type="text"
                placeholder="Event Venue"
                value={eventVenue}
                onChange={(e) => setEventVenue(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </form>

        <hr className="border-t-4 border-gray-300 my-4"></hr>

        {/* Header Image Section */}
        <div className="mb-6">
          <label className="block mb-2 font-semibold">Event Header Image:</label>
          <div className="flex justify-center gap-2 mb-4">
            <button
              type="button"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => headerImageInputRef.current.click()}
            >
              {headerImage ? 'Reupload Header Image' : 'Upload Header Image'}
            </button>

            {headerImage && (
              <button
                type="button"
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={async () => {
                  const confirm = await Swal.fire({
                    text: 'Are you sure you want to delete the header image?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    reverseButtons: true,
                  });

                  if (confirm.isConfirmed) {
                    try {
                      const res = await fetch(`/api/events/${id}/delete-header`, {
                        method: "DELETE",
                      });

                      const data = await res.json();
                      if (data.success) {
                        setHeaderImage(""); // clear from state after successful delete
                        Swal.fire({ text: data.message, icon: 'success' });
                      } else {
                        Swal.fire({ text: data.message || "Failed to delete image", icon: 'error' });
                      }
                    } catch (err) {
                      console.error("Delete error:", err);
                      Swal.fire({ text: "Error deleting header image", icon: 'error' });
                    }
                  }
                }}
              >
                Remove Image
              </button>
            )}
          </div>
          {headerImage && (
            <div className="mb-6 flex justify-center">
              <img
                src={headerImage}
                alt="Event Header"
                className="max-w-full h-auto rounded border border-gray-300"
                style={{ maxHeight: '350px' }}
              />
            </div>
          )}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            ref={headerImageInputRef}
            style={{ display: "none" }}
            onChange={handleHeaderImageUpload}
          />
        </div>

        {/* <div className="mb-6 w-full">
          <label htmlFor="messageInput" className="block mb-2 font-semibold">
            Invitation message
          </label>
          <div className="flex space-x-4 mb-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="messageType"
                value="textonly"
                checked={messageType === "textonly"}
                onChange={() => setMessageType("textonly")}
                className="form-radio"
              />
              <span className="ml-2">Text Only</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                name="messageType"
                value="image"
                checked={messageType === "image"}
                onChange={() => setMessageType("image")}
                className="form-radio"
              />
              <span className="ml-2">Image</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                name="messageType"
                value="pdf"
                checked={messageType === "pdf"}
                onChange={() => setMessageType("pdf")}
                className="form-radio"
              />
              <span className="ml-2">PDF</span>
            </label>
          </div> */}
          
          {/* Select existing message */}
          {/* {invitationMessage.length > 0 && (
            <div className="mb-4">
              <select
                value={selectedMessageId}
                onChange={(e) => {
                  const msgId = e.target.value;
                  setSelectedMessageId(msgId);
            
                  // find the selected message
                  const selected = invitationMessage.find(m => m._id === msgId);
                  console.log("Selected message:", selected);
                  if (selected) {
                    setMessageType(selected.type);
                    setMessageInput(selected.message || "");
                    setFileUrl(selected.url || "");
                  }
                }}
                className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">-- Select a saved message --</option>
                {invitationMessage.map((msg) => (
                  <option key={msg._id} value={msg._id}>
                    {msg.type === "textonly" ? msg.message.slice(0, 50) : `${msg?.type?.toUpperCase()} - ${msg.url}`}
                  </option>
                ))}
              </select>
            </div>
          )} */}

          {/* <textarea 
            value={messageInput}
            id="messageInput"
            type="text" 
            className="w-full h-60 border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Type your invitation message here"
            onChange={(e) =>
              setMessageInput(e.target.value)
            }
          />
          
          {(messageType === "image" || messageType === "pdf") && (
            <input
              value={fileUrl}
              id="messageInput"
              type="url"
              className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={`Enter the URL of the ${messageType}`}
              onChange={(e) => setFileUrl(e.target.value)}
            />
          )} */}

          {/* Update & Save Button */}
          {/* <div className="flex justify-end">
            {selectedMessageId && (
              <button
                type="button"
                className="mt-3 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={handleUpdateMessage}
              >
                Update Message
              </button>
            )}
            &nbsp;&nbsp;
            <button
              type="button"
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              onClick={handleSaveMessage}
            >
              Save Message
            </button>
          </div> */}
        {/* </div> */}

        <hr className="border-t-4 border-gray-300 my-4"></hr>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">Reupload Attendee List in Excel (ic, name):</label>
          {/* <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} /> */}
          <button
            type="button"
            className="px-4 py-2 bg-blue-500 text-white rounded"
            onClick={handleUploadClick}
          >
            Upload Excel
          </button>
          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
        </div>

        {eventName && (
          <>
          <h3 className="flex text-lg font-semibold mb-4 mt-2">List of Attendees:</h3>
            <div className="flex items-center gap-4 mb-4">
              {/* Search */}
              <input
                type="text"
                placeholder="Search by Name or Attendee ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-grow border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              />

              {/* Download button */}
              <button
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                onClick={() => downloadExcel(filteredAttendees)} // or downloadCSV
              >
                Download Attendees
              </button>
            </div>

            <div className="overflow-x-auto border rounded max-h-96">
              <table className="min-w-full table-auto border-collapse border border-gray-300 text-xs">
                <thead className="bg-gray-200 sticky top-0">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2">No</th>
                    <th className="border border-gray-300 px-4 py-2">NRIC</th>
                    <th 
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("name")}
                    >
                      Name {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2">Gender</th>
                    <th
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("dept")}
                    >
                      Dept {sortField === "dept" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th 
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("attendee_id")}
                    >
                      Attendee ID {sortField === "attendee_id" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2">Email</th>
                    <th className="border border-gray-300 px-4 py-2">Phone</th>
                    <th 
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("table")}
                    >
                      Table {sortField === "table" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th 
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("rsvp")}
                    >
                      RSVP Status {sortField === "rsvp" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2">Best Dress</th>
                    <th className="border border-gray-300 px-4 py-2">Vegetarian</th>
                    <th className="border border-gray-300 px-4 py-2">WhatsApp</th>
                    <th 
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("checkin")}
                    >
                      Check In Status {sortField === "checkin" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th 
                      className="border border-gray-300 px-4 py-2"
                      onClick={() => handleSort("send_status")}
                    >
                      Send Status {sortField === "send_status" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="border border-gray-300 px-4 py-2">Action</th>
                    <th className="border border-gray-300 px-4 py-2">
                      <input 
                        type="checkbox" 
                        style={{cursor: "pointer" }}
                        className="form-checkbox h-3 w-3 text-indigo-600 transition duration-150 ease-in-out" 
                        checked={selectedCheckbox.length === attendees.length && attendees.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendees.map((attendee, i) => (
                    <tr key={i} className="even:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">{i+1 || ""}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.nric || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.name || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.gender || getGenderFromNric(attendee.nric) || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.dept || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.attendee_id || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.email || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.phone || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.table || "-"}</td>
                      {/* <td className="border border-gray-300 px-4 py-2">{attendee.rsvp_status || "-"}</td>
                      <td className="border border-gray-300 px-4 py-2">{attendee.checkin_status || "-"}</td> */}
                      <td className="border border-gray-300 px-4 py-2">
                        {RSVP_STATUS[attendee.rsvp?.status] || "-"}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {formatYesNo(attendee.rsvp?.best_dress)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {formatYesNo(attendee.rsvp?.vegetarian)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {formatYesNo(attendee.rsvp?.whatsapp)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {CHECKIN_STATUS[attendee.checkin?.status] || "-"}
                      </td>
                      <td style={{cursor: "pointer" }} className="border border-gray-300 px-4 py-2" title={attendee.message?.message || "RSVP not yet sent"}>
                        {attendee.message && Object.keys(attendee.message).length > 0
                          ? attendee.message.success === true
                            ? "Sent Successfully"
                            : attendee.message.success === false
                            ? "Send Failed"
                            : "Send Failed"
                          : "Not Sent"
                        }
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <button
                          className="bg-pink-600 text-white px-3 py-1 rounded hover:bg-pink-700"
                          onClick={() => {
                            setEditingAttendee(attendee);
                            setShowQR(true);
                          }}
                        >
                          QR
                        </button>
                        &nbsp;&nbsp;
                        <button
                          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          onClick={() => {
                            setEditingAttendee(attendee);
                            setIsModalOpen(true);
                          }}
                        >
                          Edit
                        </button>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <input 
                          type="checkbox" 
                          style={{cursor: "pointer" }}
                          className="form-checkbox h-3 w-3 text-indigo-600 transition duration-150 ease-in-out" 
                          checked={selectedCheckbox.includes(attendee.user_id)}
                          onChange={() => handleSelectCheckbox(attendee.user_id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {isModalOpen && editingAttendee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded shadow-lg w-[480px] max-w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">
                {isAddMode ? "Add Attendee" : "Edit Attendee"}
              </h2>

              <label className="block text-left mb-4">
                NRIC:
                <input
                  type="text"
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.nric}
                  onChange={(e) =>
                    setEditingAttendee({
                      ...editingAttendee,
                      nric: e.target.value,
                      gender: getGenderFromNric(e.target.value),
                    })
                  }
                />
              </label>

              <label className="block text-left mb-4">
                Gender:
                <select
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.gender || ""}
                  onChange={(e) =>
                    setEditingAttendee({ ...editingAttendee, gender: e.target.value })
                  }
                >
                  <option value="">-</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </label>

              <label className="block text-left mb-4">
                Name:
                <input
                  type="text"
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.name}
                  onChange={(e) =>
                    setEditingAttendee({ ...editingAttendee, name: e.target.value })
                  }
                />
              </label>

              <label className="block text-left mb-4">
                Email:
                <input
                  type="email"
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.email}
                  onChange={(e) =>
                    setEditingAttendee({ ...editingAttendee, email: e.target.value })
                  }
                />
              </label>

              <label className="block text-left mb-4">
                Phone:
                <input
                  type="tel"
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.phone}
                  onChange={(e) =>
                    setEditingAttendee({ ...editingAttendee, phone: e.target.value })
                  }
                />
              </label>

              <label className="block text-left mb-4">
                Table:
                <input
                  type="table"
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.table}
                  onChange={(e) =>
                    setEditingAttendee({ ...editingAttendee, table: e.target.value })
                  }
                />
              </label>

              <label className="block text-left mb-4">
                RSVP Status:
                <select
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.rsvp?.status || 0}
                  onChange={(e) =>
                    setEditingAttendee({ 
                      ...editingAttendee, 
                      rsvp: { ...editingAttendee.rsvp, status: parseInt(e.target.value) } 
                    })
                  }
                >
                  <option value={0}>Pending</option>
                  <option value={1}>Attended</option>
                  <option value={2}>Not Attend</option>
                </select>
              </label>

              <label className="block text-left mb-4">
                Best Dress:
                <select
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.rsvp?.best_dress === true ? "yes" : editingAttendee.rsvp?.best_dress === false ? "no" : ""}
                  onChange={(e) =>
                    setEditingAttendee({
                      ...editingAttendee,
                      rsvp: {
                        ...editingAttendee.rsvp,
                        best_dress: e.target.value === "yes" ? true : e.target.value === "no" ? false : null
                      }
                    })
                  }
                >
                  <option value="">-</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>

              <label className="block text-left mb-4">
                Vegetarian:
                <select
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.rsvp?.vegetarian === true ? "yes" : editingAttendee.rsvp?.vegetarian === false ? "no" : ""}
                  onChange={(e) =>
                    setEditingAttendee({
                      ...editingAttendee,
                      rsvp: {
                        ...editingAttendee.rsvp,
                        vegetarian: e.target.value === "yes" ? true : e.target.value === "no" ? false : null
                      }
                    })
                  }
                >
                  <option value="">-</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>

              <label className="block text-left mb-4">
                Check In Status:
                <select
                  className="w-full border px-3 py-2 rounded"
                  value={editingAttendee.checkin?.status || 0}
                  onChange={(e) => 
                    setEditingAttendee({ 
                      ...editingAttendee, 
                      checkin: { ...editingAttendee.checkin, status: parseInt(e.target.value) }
                    })
                  }
                >
                  <option value={0}>Pending</option>
                  <option value={1}>Checked In</option>
                </select>
              </label>  

              <div className="flex justify-end space-x-4">
                <button
                  className="px-4 py-2 bg-gray-300 rounded"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>

                {/* Delete button (only for edit mode) */}
                {!isAddMode && (
                  <button
                    className="px-4 py-2 bg-red-600 text-white rounded"
                    onClick={async () => {
                      const confirmDelete = await Swal.fire({
                        text: "Are you sure? This attendee will be deleted permanently.",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Yes",
                        reverseButtons: true,
                      });

                      if (confirmDelete.isConfirmed) {
                        try {
                          const res = await fetch(`/api/events/${id}/attendees/${editingAttendee.user_id}`, {
                            method: "DELETE",
                          });

                          if (!res.ok) {
                            const errData = await res.json();
                            throw new Error(errData.error || "Failed to delete attendee");
                          }

                          // Remove from local state
                          const updatedAttendees = attendees.filter(
                            (att) => att.user_id !== editingAttendee.user_id
                          );
                          setAttendees(updatedAttendees);
                          setIsModalOpen(false);

                          Swal.fire({
                            text: "Attendee deleted successfully!",
                            icon: "success",
                          });
                        } catch (error) {
                          console.error(error);
                          Swal.fire({
                            text: "Error deleting attendee: " + error.message,
                            icon: "error",
                          });
                        }
                      }
                    }}
                  >
                    Delete
                  </button>
                )}

                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                  onClick={async () => {
                    try {
                      if (isAddMode) {
                        // Add new attendee to the local state
                        const updatedAttendees = [...attendees, editingAttendee];
                        setAttendees(updatedAttendees);

                        await handleUpdate(updatedAttendees);
                      }
                      else {
                        // Send only edited attendee data to backend
                        const res = await fetch(`/api/events/${id}/attendees/${editingAttendee.user_id}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(editingAttendee),
                        });
                  
                        if (!res.ok) {
                          const errData = await res.json();
                          throw new Error(errData.error || "Failed to update attendee");
                        }
                  
                        // Update local state
                        const updatedAttendees = attendees.map((att) =>
                          att.user_id === editingAttendee.user_id ? editingAttendee : att
                        );
                        setAttendees(updatedAttendees);
                      }
                      setIsModalOpen(false);
                
                      Swal.fire({
                        text: isAddMode
                          ? "Attendee added successfully!"
                          : "Attendee updated successfully!",
                        icon: "success",
                      });
                    } catch (error) {
                      console.error(error);
                      Swal.fire({
                        text: "Error saving attendee: " + error.message,
                        icon: 'error'
                      });
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Modal */}
        {showQR && editingAttendee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-lg w-[480px] max-w-full text-center">
              <h2 className="text-xl font-bold mb-4">QR Code for {editingAttendee.name}</h2>
              
              {qrSrc ? (
                <img
                  src={qrSrc}
                  alt="QR Code"
                  className="mx-auto w-50 sm:w-50 md:w-50 lg:w-60 max-w-full"
                />
              ) : (
                <p>Generating QR...</p>
              )}
              
              <button
                className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={() => setShowQR(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={handleAddAttendee}
            className="w-40 font-bold bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition duration-200"
          >
            Add Attendee
          </button>
          &nbsp;&nbsp;
          {/* <button
            onClick={handleSendMessage}
            className="w-40 font-bold bg-green-600 text-white py-2 rounded hover:bg-green-700 transition duration-200"
          >
            Send RSVP
          </button> */}
          &nbsp;&nbsp;
          <button
            onClick={handleSendQR}
            className="w-40 font-bold bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition duration-200"
          >
            Send Ticket
          </button>
          {/* &nbsp;&nbsp;
          <button
            onClick={() => handleUpdate()}
            className="w-40 font-bold bg-yellow-500 text-white py-2 rounded hover:bg-yellow-600 transition duration-200"
          >
            Update
          </button> */}
        </div>
      </div>
    </div>
  );
}

export default EventDetails;